import { Box, Typography } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "translate/Translate";
import GLButton from "components/v3/sharedComponents/GLButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Model, Question, ItemValue, IPage } from "survey-core";

type SurveySummaryProps = {
  onBack: () => void;
  onSubmit: () => void;
  surveyModel: Model;
};

type QuestionsByPage = {
  page: IPage;
  questions: {
    key: string;
    question: Question;
    answer: string;
  }[];
}[];

const formatAnswer = (question: Question, value: any): string => {
  if (question.getType() === "boolean") {
    return value ? "Yes" : "No";
  }

  // Handle slider/rating questions
  if (question.getType() === "rating" || question.getType() === "slider") {
    // Check for customLabels first (used by sliders)
    const customLabels = (question as any).customLabels || [];
    if (customLabels.length > 0) {
      const customLabel = customLabels.find((c: any) => c.value === value);
      if (customLabel?.text) {
        return customLabel.text;
      }
    }

    // Fallback to choices if no customLabels
    const choices = question.choices || [];
    const choice = choices.find((c: ItemValue) => c.value === value);
    if (choice?.text) {
      return choice.text;
    }
    // Fallback to value if no text found
    return value?.toString() || "";
  }

  const choices = question.choices || [];

  if (Array.isArray(value)) {
    return value
      .map((v) => {
        const choice = choices.find((c: ItemValue) => c.value === v);
        if (choice?.text && (choice as any).description) {
          return `${choice.text} - ${(choice as any).description}`;
        }
        return choice?.text || v;
      })
      .join(", ");
  }

  const choice = choices.find((c: ItemValue) => c.value === value);
  if (choice?.text && (choice as any).description) {
    return `${choice.text} - ${(choice as any).description}`;
  }
  return choice?.text || value;
};

const isQuestionVisible = (question?: Question): boolean => {
  return Boolean(
    question?.isVisible &&
      question?.page.isVisible &&
      (!question?.parent || question.parent.isVisible)
  );
};

const groupQuestionsByPage = (
  surveyModel: Model,
  surveyDataKeys: string[]
): QuestionsByPage => {
  const questionsByPage: QuestionsByPage = [];

  surveyDataKeys.forEach((key) => {
    const question = surveyModel.getQuestionByName(key);
    const answer = formatAnswer(question, surveyModel.data[key]);
    const page = question.page;

    const pageGroup = questionsByPage.find((group) => group.page === page);
    if (pageGroup) {
      pageGroup.questions.push({ key, question, answer });
    } else {
      questionsByPage.push({
        page,
        questions: [{ key, question, answer }]
      });
    }
  });

  return questionsByPage;
};

const SurveySummary: React.FC<SurveySummaryProps> = ({
  onBack,
  onSubmit,
  surveyModel
}) => {
  const { data: surveyData = {} } = surveyModel;

  const surveyDataKeys = Object.keys(surveyData).filter((key) => {
    const question = surveyModel.getQuestionByName(key);
    return isQuestionVisible(question) && !key.includes("do-not-save");
  });

  const questionsByPage = groupQuestionsByPage(surveyModel, surveyDataKeys);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          borderBottom: `4px solid ${COLORS.ACCENT3}`,
          paddingBottom: "30px",
          marginBottom: "30px",
          marginTop: "20px"
        }}
      >
        <Typography sx={{ typography: { xs: "body", md: "body_lg" } }}>
          Please confirm your answers below to continue to your recommendations.
          Double check your answers before continuing!
        </Typography>
        {questionsByPage.length > 0 && (
          <Box
            component="ul"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              padding: 0
            }}
          >
            {questionsByPage.map((pageGroup, pageIndex) => (
              <Box
                key={pageIndex}
                sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
              >
                {((pageGroup.page as any).title || pageGroup.page.name) && (
                  <Typography
                    variant="h5_primary"
                    sx={{
                      borderTop:
                        pageIndex === 0
                          ? `1px solid ${COLORS.ACCENT3}`
                          : `1px solid ${COLORS.GRAY}`,
                      paddingTop: pageIndex === 0 ? "33px" : "60px",
                      paddingBottom: "8px",
                      marginBottom: "8px",
                      marginTop: pageIndex === 0 ? 0 : "10px"
                    }}
                  >
                    {(pageGroup.page as any).title || pageGroup.page.name}
                  </Typography>
                )}
                {pageGroup.questions.map(({ key, question, answer }) => (
                  <Box
                    key={key}
                    component="li"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px"
                    }}
                  >
                    <Typography variant="body_strong">
                      {question.title}
                    </Typography>
                    <Typography variant="body_dark_gray">{answer}</Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
          gap: { xs: "12px", md: 0 },
          justifyContent: "space-between",
          width: "100%",
          marginBottom: "60px"
        }}
      >
        <Box
          onClick={onBack}
          sx={{
            alignItems: "center",
            cursor: "pointer",
            display: "flex",
            gap: "12px"
          }}
        >
          <Box
            sx={{
              display: "flex",
              color: COLORS.ACCENT1_DARKER,
              alignItems: "center",
              marginLeft: "8px"
            }}
          >
            <ArrowBackIcon />
          </Box>
          <Typography variant="body_strong_accent1_darker">
            <Translate text="Back" />
          </Typography>
        </Box>
        <Box>
          <GLButton
            id="confirm-survey"
            buttonType="primary"
            onClick={onSubmit}
            rightIcon={<ArrowForwardIcon />}
            text="Confirm and View Suggestions"
          />
        </Box>
      </Box>
    </>
  );
};

export default SurveySummary;
